import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Validation constants for name fields
 */
export const NAME_VALIDATION = {
  REGEX: /^[A-Za-z]([A-Za-z\s'-]*[A-Za-z])?$/,
  MIN_LENGTH: 2,
  MAX_LENGTH: 50,
} as const;

/**
 * Validate a name field (first name or last name)
 * @param name - The name value to validate
 * @param fieldName - The display name (e.g., "First name", "Last name")
 * @returns Error message if validation fails, undefined if valid
 */
export function validateName(name: string, fieldName: string = "Name"): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return `${fieldName} is required`;
  if (!NAME_VALIDATION.REGEX.test(trimmed)) return `${fieldName} can't include numbers, spaces, or symbols`;
  if (trimmed.length < NAME_VALIDATION.MIN_LENGTH) return `Must be at least ${NAME_VALIDATION.MIN_LENGTH} characters`;
  if (trimmed.length > NAME_VALIDATION.MAX_LENGTH) return `Must not exceed ${NAME_VALIDATION.MAX_LENGTH} characters`;
  return undefined;
}

/**
 * Validate a phone number (checks if it's not empty - library handles format validation)
 * @param phone - The phone number value
 * @returns Error message if validation fails, empty string if valid
 */
export function validatePhoneNumber(phone: string): string {
  if (!phone || phone.trim().length === 0) {
    return "Phone number is required";
  }
  return "";
}

/**
 * Constants for pagination limits
 */
export const PAGINATION_LIMITS = {
  MAX_PAGES: 400, // Maximum pages that can be navigated
  MAX_RECORDS: 10000, // Maximum records accessible (400 * 25)
  DEFAULT_PAGE_SIZE: 25,
} as const;

/**
 * Apply pagination limits to total pages
 * @param totalPages - Total pages from API
 * @returns Limited total pages (max 400)
 */
export function applyPaginationLimit(totalPages: number): number {
  return Math.min(totalPages, PAGINATION_LIMITS.MAX_PAGES);
}

/**
 * Check if pagination limit is reached
 * @param totalPages - Total pages from API
 * @returns true if limit is reached
 */
export function isPaginationLimitReached(totalPages: number): boolean {
  return totalPages > PAGINATION_LIMITS.MAX_PAGES;
}

/**
 * Get maximum accessible records based on items per page
 * @param itemsPerPage - Items per page
 * @returns Maximum accessible records
 */
export function getMaxAccessibleRecords(itemsPerPage: number): number {
  return PAGINATION_LIMITS.MAX_PAGES * itemsPerPage;
}

export type PaginationRangeItem = number | "ellipsis";

/**
 * Page numbers to render around the current page, always including the
 * first and last page (e.g. 1 2 3 4 5 ... 400, or 1 ... 198 199 200 ... 400).
 * Falls back to every page when the whole range already fits without gaps.
 */
export function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount = 1
): PaginationRangeItem[] {
  const totalPageNumbers = siblingCount * 2 + 5; // first + last + current + 2 siblings + 2 ellipses
  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, "ellipsis", totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );
    return [1, "ellipsis", ...rightRange];
  }

  const middleRange = Array.from(
    { length: rightSiblingIndex - leftSiblingIndex + 1 },
    (_, i) => leftSiblingIndex + i
  );
  return [1, "ellipsis", ...middleRange, "ellipsis", totalPages];
}

export function formatDate(value?: string | number | Date): string {
  if (value === undefined || value === null || value === '') return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function humanizeNumber(num: number | null | undefined): string {
  if (num == null) return '';
  if (num < 1000) return num.toString();
  if (num < 1000000) {
    const thousands = num / 1000;
    const decimalPlaces = num % 1000 === 0 ? 0 : 2;
    const truncated = Math.floor(thousands * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
    return truncated.toFixed(decimalPlaces) + 'k+';
  }
  if (num < 1000000000) {
    const millions = num / 1000000;
    const decimalPlaces = num % 1000000 === 0 ? 0 : 2;
    const truncated = Math.floor(millions * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
    return truncated.toFixed(decimalPlaces) + 'M+';
  }
  const billions = num / 1000000000;
  const decimalPlaces = num % 1000000000 === 0 ? 0 : 2;
  const truncated = Math.floor(billions * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
  return truncated.toFixed(decimalPlaces) + 'B+';
}